import { AlertCircle, Save, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Configuration } from '../../services/configurationService';
import {
  CreateConfigurationData,
  dropdownAdminService,
  UpdateConfigurationData,
} from '../../services/dropdownAdminService';
import { Dropdown, Input, TextArea } from '../forms';

interface ConfigurationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  config?: Configuration | null;
  categories: string[];
}

const ConfigurationDrawer: React.FC<ConfigurationDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  config,
  categories,
}) => {
  const [formData, setFormData] = useState({
    key: '',
    type: 'dropdown' as 'dropdown' | 'tools' | 'directories' | 'settings',
    category: '',
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [keyTouched, setKeyTouched] = useState(false);

  const isEditing = !!config;

  useEffect(() => {
    if (config) {
      setFormData({
        key: config.key,
        type: config.type,
        category: config.category,
        name: config.name,
        description: config.description || '',
      });
    } else {
      setFormData({
        key: '',
        type: 'dropdown',
        category: '',
        name: '',
        description: '',
      });
    }
    setErrors({});
    setKeyTouched(false);
  }, [config, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Key validation
    if (!formData.key) {
      newErrors.key = 'Key is required';
    } else {
      const keyValidation = dropdownAdminService.validateConfigurationKey(formData.key);
      if (!keyValidation.isValid) {
        newErrors.key = keyValidation.error || 'Invalid key format';
      }
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Name must be less than 200 characters';
    }

    // Category validation
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    } else if (formData.category.length > 100) {
      newErrors.category = 'Category must be less than 100 characters';
    }

    // Description validation
    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isEditing) {
        const updateData: UpdateConfigurationData = {
          type: formData.type,
          category: formData.category,
          name: formData.name,
          description: formData.description || undefined,
        };
        await dropdownAdminService.updateConfiguration(formData.key, updateData);
      } else {
        const createData: CreateConfigurationData = {
          key: formData.key,
          type: formData.type,
          category: formData.category,
          name: formData.name,
          description: formData.description || undefined,
          options: formData.type === 'dropdown' ? [] : undefined,
        };
        await dropdownAdminService.createConfiguration(createData);
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      setErrors({ submit: error.message || 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData(prev => ({ ...prev, name }));

    // Auto-generate key from name if creating new config and key hasn't been manually touched
    if (!isEditing && !keyTouched) {
      const generatedKey = dropdownAdminService.generateKeyFromName(name);
      setFormData(prev => ({ ...prev, key: generatedKey }));
    }
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyTouched(true);
    setFormData(prev => ({ ...prev, key: e.target.value }));
  };

  const typeOptions = [
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'tools', label: 'Tools' },
    { value: 'directories', label: 'Directories' },
    { value: 'settings', label: 'Settings' },
  ];

  const categoryOptions = [
    ...categories.map(cat => ({
      value: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
    })),
    { value: 'custom', label: 'Create New Category...' },
  ];

  if (!isOpen) return null;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[110]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 w-full max-w-2xl bg-white shadow-xl rounded-xl border border-gray-200 z-[120] flex flex-col">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <Save className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Configuration' : 'Create New Configuration'}
              </h3>
              <p className="text-sm text-gray-600">
                {isEditing
                  ? 'Update configuration details and settings'
                  : 'Create a new dropdown configuration for the system'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-red-700">{errors.submit}</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Configuration Name"
                value={formData.name}
                onChange={handleNameChange}
                error={errors.name}
                placeholder="e.g., Project Status Options"
                hint="Descriptive name for this configuration"
                required
              />

              <Input
                label="Configuration Key"
                value={formData.key}
                onChange={handleKeyChange}
                error={errors.key}
                placeholder="e.g., project-statuses"
                hint="Unique identifier (lowercase, hyphens, underscores)"
                disabled={isEditing}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Dropdown
                label="Type"
                options={typeOptions}
                value={formData.type}
                onChange={value => setFormData(prev => ({ ...prev, type: value as any }))}
                error={errors.type}
                disabled={isEditing}
                required
              />

              <div>
                <Dropdown
                  label="Category"
                  options={categoryOptions}
                  value={formData.category}
                  onChange={value => {
                    if (value === 'custom') {
                      const customCategory = prompt('Enter new category name:');
                      if (customCategory) {
                        setFormData(prev => ({ ...prev, category: customCategory.toLowerCase() }));
                      }
                    } else {
                      setFormData(prev => ({ ...prev, category: value }));
                    }
                  }}
                  error={errors.category}
                  placeholder="Select or create category"
                  required
                />
                {formData.category && !categories.includes(formData.category) && (
                  <p className="mt-1 text-sm text-red-600">
                    This will create a new category: "{formData.category}"
                  </p>
                )}
              </div>
            </div>

            <TextArea
              label="Description"
              value={formData.description}
              onChange={(value: string) => setFormData(prev => ({ ...prev, description: value }))}
              error={errors.description}
              placeholder="Brief description of this configuration's purpose..."
              rows={3}
              hint="Optional description to help other admins understand this configuration"
            />

            {formData.type === 'dropdown' && !isEditing && (
              <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-brand-primary mt-0.5 flex-shrink-0" />
                  <div className="text-brand-700 text-sm">
                    <p className="font-medium">Dropdown Configuration</p>
                    <p>
                      After creating this configuration, you'll be able to add dropdown options
                      through the options management interface.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Sticky Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-white rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default ConfigurationDrawer;
